/**
 * Product page behaviour (loaded with defer by sections/main-product and
 * sections/product-recommendations).
 *
 * <product-info>       Coordinates a product section: listens for
 *                      `variant:select`, updates price, SKU, stock, buttons,
 *                      URL (?variant=), gallery, sticky bar and bundle.
 * <variant-picker>     Per-option radios; hierarchical availability (values
 *                      with no purchasable variant are disabled) and smart
 *                      auto-adjust of later options.
 * <product-gallery>    Scroll-snap carousel, thumbnails, dots, arrows.
 * <deferred-media>     Mounts video / 3D model on demand.
 * <product-modal>      <dialog> wrapper with focus trap.
 *                      Exposed as window.ThemeProductModal for
 *                      <product-lightbox> (assets/product-lightbox.js).
 * <product-form>       AJAX add to cart: busy -> added states, dispatches
 *                      `cart:updated` + `cart:item-added` (header count, toast).
 * <quantity-input>     Stepper.
 * <sticky-atc>         Mobile bar shown once the main button scrolls away.
 * <delivery-estimate>  Localised delivery dates (skips weekends).
 * <product-bundle>     "Complete the set": complementary recommendations
 *                      with checkboxes, one-click add via /cart/add.js items[].
 * <product-recommendations> Lazy related products (Section Rendering API).
 */
(() => {
  // main-product + product-recommendations both include this file.
  if (window.themeProductLoaded) return;
  window.themeProductLoaded = true;

  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    getComputedStyle(document.documentElement).getPropertyValue('--motion-scale').trim() === '0';
  const scrollBehavior = () => (reduceMotion() ? 'auto' : 'smooth');
  const money = (cents) => (window.theme && window.theme.formatMoney ? window.theme.formatMoney(cents) : String(cents));
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Shared cart API from theme.js (add.js + drawer section + fresh cart.js).
  const cartAdd = (payload) => window.theme.cartAdd(payload);

  const announceCart = (cart, item, source, sections = null) => {
    window.theme.announceAdd({ item, cart, sections, source });
  };

  /* ---------------------------------------------------------------------- */
  /* <product-info>                                                         */
  /* ---------------------------------------------------------------------- */
  define(
    'product-info',
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-product-json]');
        this.variants = json ? JSON.parse(json.textContent).variants : [];
        this.addEventListener('variant:select', (event) => this.onVariant(event.detail));
        this.bindStickyColumns();
        this.bindLivePrice();
      }

      /* Live price: unit x quantity, plus the automatic bundle discount
         (shop metafield cuddle.bundle_discount) once cart quantity + the
         selected quantity reaches minQuantity. */
      bindLivePrice() {
        this.live = this.querySelector('[data-price-live]');
        if (!this.live) return;
        const input = this.querySelector('[data-variant-input]');
        this.currentVariant = this.variants.find((v) => String(v.id) === String(input && input.value)) || this.variants[0];
        this.cartCount = Number(this.live.dataset.initialCartCount) || 0;
        this.addEventListener('quantity:change', () => this.updateLivePrice());
        this.onCartUpdated = (event) => {
          if (event.detail && typeof event.detail.item_count === 'number') {
            this.cartCount = event.detail.item_count;
            this.updateLivePrice();
          }
        };
        document.addEventListener('cart:updated', this.onCartUpdated);
        // Fresh count (back/forward cache, other tabs).
        if (window.theme && window.theme.fetchCart) {
          window.theme
            .fetchCart()
            .then((cart) => {
              this.cartCount = cart.item_count;
              this.updateLivePrice();
            })
            .catch(() => {});
        }
        this.updateLivePrice();
      }

      quantity() {
        const input = this.querySelector('input[name="quantity"]');
        return Math.max(1, parseInt(input && input.value, 10) || 1);
      }

      updateLivePrice() {
        const wrap = this.live;
        const variant = this.currentVariant;
        if (!wrap || !variant) return;
        const qty = this.quantity();
        const percent = Number(wrap.dataset.percent) || 0;
        const min = Number(wrap.dataset.min) || 2;
        const unit = Number(variant.price) || 0;
        const total = unit * qty;
        const applies = percent > 0 && variant.available && this.cartCount + qty >= min;
        const saving = applies ? Math.floor((total * percent) / 100) : 0;
        const now = total - saving;
        const onSale = !applies && Number(variant.compareAt) > unit;
        const fill = (template, values) =>
          Object.keys(values).reduce((text, key) => text.split(`__${key}__`).join(values[key]), template || '');

        wrap.querySelectorAll('[data-price-current]').forEach((el) => (el.textContent = money(now)));
        wrap.querySelectorAll('[data-price-compare], [data-sale-label]').forEach((el) => (el.hidden = !onSale));
        wrap.querySelectorAll('[data-compare-value]').forEach((el) => (el.textContent = onSale ? money(variant.compareAt * qty) : ''));
        wrap.querySelectorAll('[data-save]').forEach((el) => {
          if (applies) el.hidden = true;
        });

        const was = wrap.querySelector('[data-live-was]');
        if (was) {
          was.hidden = !applies;
          was.textContent = applies ? money(total) : '';
        }

        const chip = wrap.querySelector('[data-live-save]');
        if (chip) {
          const text = applies ? fill(wrap.dataset.textSave, { amount: money(saving), percent: String(percent) }) : '';
          const changed = chip.textContent !== text;
          chip.hidden = !applies;
          chip.textContent = text;
          if (applies && changed && !reduceMotion()) {
            chip.classList.remove('is-pop');
            void chip.offsetWidth;
            chip.classList.add('is-pop');
          }
        }

        const each = wrap.querySelector('[data-live-each]');
        if (each) {
          each.hidden = qty < 2;
          each.textContent = qty < 2 ? '' : fill(wrap.dataset.textEach, { price: money(unit) });
        }

        const nudge = wrap.querySelector('[data-live-nudge]');
        if (nudge) {
          const remaining = min - this.cartCount - qty;
          const show = percent > 0 && variant.available && remaining > 0;
          nudge.hidden = !show;
          const label = nudge.querySelector('[data-live-nudge-text]');
          if (label && show) {
            label.textContent = fill(remaining === 1 ? wrap.dataset.textNudgeOne : wrap.dataset.textNudgeOther, {
              count: String(remaining),
              percent: String(percent),
            });
          }
        }

        if (Number.isFinite(now) && now > 0) {
          this.querySelectorAll('[data-submit-price], [data-sticky-price]').forEach((el) => (el.textContent = money(now)));
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.onCartUpdated) document.removeEventListener('cart:updated', this.onCartUpdated);
      }

      /* Columns taller than the viewport stick by their bottom edge. */
      bindStickyColumns() {
        if (!('ResizeObserver' in window)) return;
        const cols = this.querySelectorAll('[data-sticky-col]');
        this.resizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const height = Math.ceil(entry.target.getBoundingClientRect().height);
            const fits = height + 48 < window.innerHeight;
            entry.target.style.setProperty('--col-height', fits ? '0px' : `${height}px`);
          });
        });
        cols.forEach((col) => this.resizeObserver.observe(col));
      }

      onVariant({ variant, initial }) {
        const available = Boolean(variant && variant.available);

        this.querySelectorAll('[data-variant-input]').forEach((input) => {
          if (variant) input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Price
        const price = this.querySelector('[data-price]');
        if (price && variant) {
          const onSale = Boolean(variant.compareHtml);
          price.classList.toggle('price--sale', onSale);
          const set = (sel, fn) => price.querySelectorAll(sel).forEach(fn);
          set('[data-price-current]', (el) => (el.textContent = variant.priceHtml));
          set('[data-compare-value]', (el) => (el.textContent = variant.compareHtml || ''));
          set('[data-price-compare], [data-sale-label]', (el) => (el.hidden = !onSale));
          set('[data-save]', (el) => {
            el.hidden = !onSale || !variant.save;
            el.textContent = (el.dataset.template || '').replace('[percent]', variant.save);
          });
        }

        // SKU
        const skuWrap = this.querySelector('[data-sku-wrap]');
        if (skuWrap) {
          const sku = variant && variant.sku ? variant.sku : '';
          skuWrap.hidden = !sku;
          const value = skuWrap.querySelector('[data-sku]');
          if (value) value.textContent = sku;
        }

        // Buttons (main + sticky)
        const label = available
          ? this.dataset.textAdd
          : variant
            ? this.dataset.textSoldOut
            : this.dataset.textUnavailable;
        this.querySelectorAll('[data-submit]').forEach((button) => {
          button.disabled = !available;
          const text = button.querySelector('[data-submit-label]');
          if (text) text.textContent = label;
          const priceEl = button.querySelector('[data-submit-price]');
          if (priceEl) {
            priceEl.hidden = !available;
            if (variant) priceEl.textContent = variant.priceHtml;
          }
        });

        this.updateStock(variant);

        // Sticky bar
        this.querySelectorAll('[data-sticky-variant]').forEach((el) => (el.textContent = variant ? variant.title : ''));
        this.querySelectorAll('[data-sticky-price]').forEach((el) => (el.textContent = variant ? variant.priceHtml : ''));

        // Spec plate rows that mirror a variant option (e.g. Scale)
        if (variant) {
          this.querySelectorAll('[data-spec-option]').forEach((el) => {
            const value = variant.options[Number(el.dataset.specOption)];
            if (value) el.textContent = value;
          });
        }

        // URL
        if (variant && !initial) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        // Gallery
        const gallery = this.querySelector('product-gallery');
        if (gallery && variant && variant.mediaId && !initial) gallery.showMedia(variant.mediaId);

        if (variant) this.currentVariant = variant;
        this.updateLivePrice();

        this.dispatchEvent(new CustomEvent('product:variant-change', { detail: { variant } }));
      }

      updateStock(variant) {
        const stock = this.querySelector('[data-stock]');
        if (!stock) return;
        const text = stock.querySelector('[data-stock-text]');
        const threshold = Number(stock.dataset.threshold) || 0;
        let state = 'in';
        let message = stock.dataset.textIn;
        if (!variant) {
          state = 'unavailable';
          message = this.dataset.textUnavailable;
        } else if (!variant.available) {
          state = 'out';
          message = this.dataset.textSoldOut;
        } else if (typeof variant.inventory === 'number' && variant.inventory <= threshold) {
          state = 'low';
          message = (stock.dataset.textLow || '').replace('[count]', variant.inventory);
        }
        stock.className = `stock stock--${state}`;
        if (text) text.textContent = message;
        stock.hidden = state === 'in' && stock.dataset.showInStock !== 'true';
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <variant-picker>                                                       */
  /* ---------------------------------------------------------------------- */
  define(
    'variant-picker',
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        this.info = this.closest('product-info');
        this.variants = this.info ? this.info.variants : [];
        this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.addEventListener('change', (event) => {
          const fieldset = event.target.closest('fieldset');
          this.onChange(this.fieldsets.indexOf(fieldset));
        });
        this.refresh(true);
      }

      selected() {
        return this.fieldsets.map((fieldset) => {
          const checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      match(values, upto, onlyAvailable) {
        return this.variants.find(
          (v) => (!onlyAvailable || v.available) && v.options.every((value, i) => i > upto || value === values[i])
        );
      }

      onChange(index) {
        const values = this.selected();
        if (!this.match(values, values.length - 1, true)) {
          // Keep the options up to the one just changed, adjust the rest.
          const fallback = this.match(values, index, true);
          if (fallback) fallback.options.forEach((value, i) => this.check(i, value));
        }
        this.refresh();
      }

      check(index, value) {
        const fieldset = this.fieldsets[index];
        if (!fieldset) return;
        const input = Array.from(fieldset.querySelectorAll('input')).find((el) => el.value === value);
        if (input) input.checked = true;
      }

      refresh(initial = false) {
        const values = this.selected();
        let hex = '';

        this.fieldsets.forEach((fieldset, index) => {
          const checked = fieldset.querySelector('input:checked');
          const display = fieldset.querySelector('[data-option-value]');
          if (display) display.textContent = checked ? checked.value : '';
          if (checked && checked.dataset.hex) hex = checked.dataset.hex;

          // Hierarchical availability: given the choices above this option.
          fieldset.querySelectorAll('input').forEach((input) => {
            const possible = this.variants.some(
              (v) => v.available && v.options[index] === input.value && v.options.every((value, i) => i >= index || value === values[i])
            );
            input.disabled = !possible && !input.checked;
            input.toggleAttribute('data-unavailable', !possible);
          });
        });

        const variant = this.variants.find((v) => v.options.every((value, i) => value === values[i]));
        if (this.info) {
          this.info.dispatchEvent(new CustomEvent('variant:select', { detail: { variant, hex, initial } }));
        }
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <product-gallery>                                                      */
  /* ---------------------------------------------------------------------- */
  define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.dots = Array.from(this.querySelectorAll('.gallery__dot'));
        this.prev = this.querySelector('.gallery__viewer [data-prev]');
        this.next = this.querySelector('.gallery__viewer [data-next]');
        this.lightbox = this.querySelector('product-lightbox');
        this.active = 0;

        this.querySelectorAll('[data-thumb]').forEach((thumb) =>
          thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumb))
        );
        if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.active - 1));
        if (this.next) this.next.addEventListener('click', () => this.goTo(this.active + 1));
        // Zoom is opt-in (section setting): without the lightbox the images
        // are not clickable at all.
        if (this.lightbox) {
          this.track.addEventListener('click', (event) => {
            const zoom = event.target.closest('[data-zoom]');
            if (zoom && typeof this.lightbox.openAt === 'function') this.lightbox.openAt(zoom.dataset.zoom, zoom);
          });
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(this.slides().indexOf(entry.target));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides().forEach((slide) => this.observer.observe(slide));
        this.setActive(0);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      slides() {
        return Array.from(this.track.children);
      }

      isGrid() {
        return getComputedStyle(this.track).display === 'grid';
      }

      goTo(index) {
        const slides = this.slides();
        const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
        if (!slide) return;
        if (this.isGrid()) {
          slide.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' });
        } else {
          this.track.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
        }
      }

      showMedia(mediaId) {
        const slides = this.slides();
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index < 0) return;
        if (this.isGrid()) {
          // Grid: bring the variant's photo to the large first spot.
          if (index > 0) {
            this.track.prepend(slides[index]);
            const thumbs = this.querySelector('[data-thumbs]');
            const thumbItem = thumbs && thumbs.querySelector(`li[data-media-id="${mediaId}"]`);
            if (thumbItem) thumbs.prepend(thumbItem);
          }
          this.setActive(0);
          return;
        }
        this.goTo(index);
        this.setActive(index);
      }

      setActive(index) {
        const slides = this.slides();
        if (index < 0 || !slides[index]) return;
        this.active = index;
        const id = slides[index].dataset.mediaId;
        slides.forEach((slide, i) => {
          if (i === index) return;
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('iframe').forEach((frame) => {
            try {
              frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              frame.contentWindow.postMessage('{"method":"pause"}', '*');
            } catch (error) {
              /* cross-origin frames may refuse; ignore */
            }
          });
        });
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        this.querySelectorAll('[data-thumb]').forEach((thumb) => {
          if (thumb.dataset.thumb === id) {
            thumb.setAttribute('aria-current', 'true');
            const rail = thumb.closest('[data-thumbs]');
            if (rail && rail.scrollHeight > rail.clientHeight + 4) {
              rail.scrollTo({ top: thumb.offsetTop - rail.clientHeight / 2, behavior: scrollBehavior() });
            }
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
        if (this.prev) this.prev.disabled = index === 0;
        if (this.next) this.next.disabled = index === slides.length - 1;
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <deferred-media>                                                       */
  /* ---------------------------------------------------------------------- */
  define(
    'deferred-media',
    class DeferredMedia extends HTMLElement {
      connectedCallback() {
        const trigger = this.querySelector('[data-deferred-trigger]');
        if (trigger) trigger.addEventListener('click', () => this.load());
      }

      load() {
        if (this.loaded) return;
        const template = this.querySelector('template');
        if (!template) return;
        this.loaded = true;
        const content = template.content.firstElementChild.cloneNode(true);
        this.replaceChildren(content);
        if (content.tagName === 'VIDEO') {
          content.play().catch(() => {});
          content.focus();
        }
        if (this.dataset.type === 'model' && window.Shopify && window.Shopify.loadFeatures) {
          window.Shopify.loadFeatures([
            {
              name: 'model-viewer-ui',
              version: '1.0',
              onLoad: (errors) => {
                if (errors || !window.Shopify.ModelViewerUI) return;
                const viewer = this.querySelector('model-viewer');
                if (viewer) {
                  const ui = new window.Shopify.ModelViewerUI(viewer);
                  if (ui && ui.play) ui.play();
                }
              },
            },
          ]);
        }
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <product-modal>                                                        */
  /* ---------------------------------------------------------------------- */
  class ProductModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.dialog.addEventListener('keydown', (event) => trapFocus(this.dialog, event));
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]')) return this.close();
        if (event.target !== this.dialog) return;
        // Clicks on the ::backdrop land on the dialog but outside its box.
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) this.close();
      });
      this.dialog.addEventListener('close', () => this.afterClose());
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      const focusTarget = this.dialog.querySelector('[data-modal-close]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    afterClose() {
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }
  }
  define('product-modal', ProductModal);
  // Base for <product-lightbox> (assets/product-lightbox.js, only loaded
  // when the section's "Enable image zoom" setting is on).
  window.ThemeProductModal = ProductModal;

  // Openers anywhere on the page: <button data-modal-open="ModalId">
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (!opener) return;
    const modal = document.getElementById(opener.dataset.modalOpen);
    if (modal && typeof modal.open === 'function') modal.open(opener);
  });

  /* ---------------------------------------------------------------------- */
  /* <quantity-input>                                                       */
  /* ---------------------------------------------------------------------- */
  define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.buttons = Array.from(this.querySelectorAll('[data-step]'));
        this.buttons.forEach((button) =>
          button.addEventListener('click', () => {
            const step = Number(button.dataset.step);
            this.set((Number(this.input.value) || 1) + step);
          })
        );
        this.input.addEventListener('change', () => this.set(Number(this.input.value) || 1));
        this.sync();
      }

      set(value) {
        const min = Number(this.input.min) || 1;
        const max = Number(this.input.max) || 99;
        this.input.value = Math.max(min, Math.min(max, Math.round(value)));
        this.sync();
        this.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true, detail: { value: Number(this.input.value) } }));
      }

      sync() {
        const value = Number(this.input.value) || 1;
        const min = Number(this.input.min) || 1;
        const max = Number(this.input.max) || 99;
        this.buttons.forEach((button) => {
          const step = Number(button.dataset.step);
          button.disabled = step < 0 ? value <= min : value >= max;
        });
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <product-form>                                                         */
  /* ---------------------------------------------------------------------- */
  define(
    'product-form',
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.info = this.closest('product-info');
        this.error = this.querySelector('[data-error]');
        this.status = this.querySelector('[data-status]');
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit(event.submitter);
        });
      }

      buttons() {
        const scope = this.info || this;
        return Array.from(scope.querySelectorAll('[data-submit]'));
      }

      async submit(submitter) {
        const buttons = this.buttons();
        if (buttons.some((button) => button.getAttribute('aria-busy') === 'true')) return;
        clearTimeout(this.resetTimer);
        this.error.hidden = true;
        buttons.forEach((button) => {
          delete button.dataset.state;
          button.setAttribute('aria-busy', 'true');
        });

        const body = new FormData(this.form);
        try {
          const { item, cart, sections } = await cartAdd(body);
          buttons.forEach((button) => {
            button.removeAttribute('aria-busy');
            button.dataset.state = 'added';
            const label = button.querySelector('[data-submit-label]');
            if (label) {
              button.dataset.label = label.textContent;
              label.textContent = this.dataset.textAdded;
            }
          });
          if (this.status) this.status.textContent = this.dataset.textAdded;
          announceCart(cart, item, submitter || buttons[0], sections);
          this.resetTimer = setTimeout(() => this.reset(), 2400);
        } catch (error) {
          buttons.forEach((button) => {
            button.removeAttribute('aria-busy');
            button.dataset.state = 'error';
          });
          this.error.textContent = error.message || this.dataset.textError;
          this.error.hidden = false;
          this.resetTimer = setTimeout(() => this.reset(), 2600);
        }
      }

      reset() {
        this.buttons().forEach((button) => {
          delete button.dataset.state;
          const label = button.querySelector('[data-submit-label]');
          if (label && button.dataset.label) label.textContent = button.dataset.label;
        });
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <sticky-atc>                                                           */
  /* ---------------------------------------------------------------------- */
  define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        const info = this.closest('product-info');
        this.target = info && info.querySelector('.product-form__submit');
        if (!this.target || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggle(passed);
        });
        this.observer.observe(this.target);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.documentElement.classList.remove('has-sticky-atc');
      }

      toggle(show) {
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        const visible = show && !desktop;
        this.classList.toggle('is-visible', visible);
        this.inert = !visible;
        document.documentElement.classList.toggle('has-sticky-atc', visible);
        if (visible) {
          document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        }
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <delivery-estimate>                                                    */
  /* ---------------------------------------------------------------------- */
  define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const skip = this.dataset.skipWeekends === 'true';
        const add = (days) => {
          const date = new Date();
          let left = Number(days) || 0;
          while (left > 0) {
            date.setDate(date.getDate() + 1);
            const day = date.getDay();
            if (!skip || (day !== 0 && day !== 6)) left -= 1;
          }
          if (skip) while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
          return date;
        };
        let format;
        try {
          format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
        } catch (error) {
          return;
        }
        const start = this.querySelector('[data-start]');
        const end = this.querySelector('[data-end]');
        if (start) start.textContent = format.format(add(this.dataset.min));
        if (end) end.textContent = format.format(add(this.dataset.max));
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <product-bundle>                                                       */
  /* ---------------------------------------------------------------------- */
  define(
    'product-bundle',
    class ProductBundle extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-list]');
        this.template = this.querySelector('[data-item-template]');
        this.totalEl = this.querySelector('[data-total]');
        this.countEl = this.querySelector('[data-count]');
        this.addButton = this.querySelector('[data-add]');
        this.status = this.querySelector('[data-status]');
        this.main = this.querySelector('[data-main]');
        this.info = this.closest('product-info');

        this.list.addEventListener('change', () => this.update());
        this.addButton.addEventListener('click', () => this.add());
        if (this.info) {
          this.info.addEventListener('product:variant-change', (event) => this.onMainVariant(event.detail.variant));
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.observer.disconnect();
              this.load();
            },
            { rootMargin: '0px 0px 600px 0px' }
          );
          // Hidden elements never intersect: observe the parent column.
          this.observer.observe(this.parentElement || this);
        } else {
          this.load();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async fetchProducts(url) {
        if (!url) return [];
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) return [];
          const data = await response.json();
          return Array.isArray(data.products) ? data.products : [];
        } catch (error) {
          return [];
        }
      }

      async load() {
        const ownId = Number(this.dataset.productId);
        const usable = (list) => list.filter((p) => p.id !== ownId && p.available && p.variants.some((v) => v.available));
        let products = usable(await this.fetchProducts(this.dataset.url));
        if (!products.length) products = usable(await this.fetchProducts(this.dataset.fallbackUrl));

        if (!products.length) {
          if (this.hasAttribute('data-design-mode')) {
            this.hidden = false;
            this.querySelector('[data-empty]').hidden = false;
            this.list.hidden = true;
            this.querySelector('.bundle__footer').hidden = true;
          }
          return;
        }

        products.forEach((product, index) => this.list.append(this.renderItem(product, index)));
        this.hidden = false;
        this.update();
      }

      renderItem(product, index) {
        const fragment = this.template.content.cloneNode(true);
        const item = fragment.querySelector('li');
        const variants = product.variants.filter((v) => v.available);
        const first = variants[0];
        item.classList.add('is-new');
        item.style.animationDelay = `${index * 80}ms`;
        item.dataset.variantId = first.id;
        item.dataset.price = first.price;

        item.querySelectorAll('[data-link]').forEach((link) => (link.href = product.url));
        item.querySelector('[data-title]').textContent = product.title;
        item.querySelector('[data-check-label]').textContent = (this.dataset.textAddItem || '').replace('[title]', product.title);

        const img = item.querySelector('[data-image]');
        const src = product.featured_image;
        if (src) {
          const url = new URL(src, window.location.href);
          url.searchParams.set('width', '192');
          img.src = url.toString();
        } else {
          img.remove();
        }

        const select = item.querySelector('[data-select]');
        if (variants.length > 1) {
          select.hidden = false;
          select.setAttribute('aria-label', (this.dataset.textOptions || '').replace('[title]', product.title));
          variants.forEach((variant) => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.dataset.price = variant.price;
            option.textContent = variant.title;
            select.append(option);
          });
          select.addEventListener('change', () => {
            const chosen = select.selectedOptions[0];
            item.dataset.variantId = chosen.value;
            item.dataset.price = chosen.dataset.price;
            item.querySelector('[data-price]').textContent = money(chosen.dataset.price);
            this.update();
          });
        } else {
          select.remove();
        }
        item.querySelector('[data-price]').textContent = money(first.price);
        return item;
      }

      onMainVariant(variant) {
        if (!this.main) return;
        const check = this.main.querySelector('[data-check]');
        const available = Boolean(variant && variant.available);
        if (variant) {
          this.main.dataset.variantId = variant.id;
          this.main.dataset.price = variant.price;
          const price = this.main.querySelector('[data-main-price]');
          if (price) price.textContent = variant.priceHtml;
          const title = this.main.querySelector('[data-main-variant]');
          if (title) title.textContent = variant.title;
        }
        check.disabled = !available;
        if (!available) check.checked = false;
        this.update();
      }

      selectedItems() {
        return Array.from(this.list.children).filter((item) => {
          const check = item.querySelector('[data-check]');
          return check && check.checked && !check.disabled;
        });
      }

      update() {
        const selected = this.selectedItems();
        Array.from(this.list.children).forEach((item) => {
          const check = item.querySelector('[data-check]');
          item.classList.toggle('is-off', !(check && check.checked));
        });
        const total = selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
        this.totalEl.textContent = money(total);
        this.countEl.textContent = selected.length ? `(${selected.length})` : '';
        this.addButton.disabled = selected.length === 0;
      }

      async add() {
        const selected = this.selectedItems();
        if (!selected.length || this.addButton.getAttribute('aria-busy') === 'true') return;
        clearTimeout(this.resetTimer);
        delete this.addButton.dataset.state;
        this.addButton.setAttribute('aria-busy', 'true');
        this.status.textContent = '';

        const items = selected.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
        try {
          const { item, cart, sections } = await cartAdd({ items });
          this.addButton.removeAttribute('aria-busy');
          this.addButton.dataset.state = 'added';
          this.status.textContent = this.dataset.textAdded;
          announceCart(cart, item, this.addButton, sections);
          this.resetTimer = setTimeout(() => delete this.addButton.dataset.state, 2400);
        } catch (error) {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.dataset.state = 'error';
          this.status.textContent = error.message || this.dataset.textError;
          this.resetTimer = setTimeout(() => delete this.addButton.dataset.state, 2600);
        }
      }
    }
  );

  /* ---------------------------------------------------------------------- */
  /* <product-recommendations>                                              */
  /* ---------------------------------------------------------------------- */
  define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.children.length || !this.dataset.url) return;
        if (!('IntersectionObserver' in window)) return this.load();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        try {
          const response = await fetch(this.dataset.url);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (!fresh || !fresh.innerHTML.trim()) return;
          this.innerHTML = fresh.innerHTML;
          if (window.theme && window.theme.initMotion) window.theme.initMotion(this);
        } catch (error) {
          /* recommendations are optional */
        }
      }
    }
  );
})();
